import { type FormEvent, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useAuth } from "../auth/AuthContext";
import { AuthLayout } from "../components/AuthLayout";
import { signInWithPassword } from "../lib/auth";
import { safeInternalReturnPath } from "../lib/loginReturnPath";

export function LoginPage() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);
  const returnTo = safeInternalReturnPath(params.get("return_to"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!authLoading && user) {
    return <Navigate to={returnTo ?? "/home/"} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithPassword(email, password);
      navigate(returnTo ?? "/home/", { replace: true });
    } catch {
      setError(
        "Inloggen is niet gelukt. Controleer je uitnodiging en gegevens.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <h1>Inloggen</h1>
      <p>Gebruik uitsluitend je vooraf uitgenodigde DBVDW-account.</p>
      {params.get("invitation") === "accepted" && (
        <p>Je uitnodiging is geaccepteerd. Je kunt nu inloggen.</p>
      )}
      {params.get("password") === "changed" && (
        <p>Je wachtwoord is gewijzigd. Je kunt nu inloggen.</p>
      )}
      <form onSubmit={handleSubmit}>
        <label htmlFor="email">E-mailadres</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <label htmlFor="password">Wachtwoord</label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button type="submit" disabled={submitting}>
          {submitting ? "Inloggen..." : "Inloggen"}
        </button>
      </form>
      <p>
        <Link to="/forgot-password/">Wachtwoord vergeten?</Link>
      </p>
      {error && <p className="error" role="alert">{error}</p>}
    </AuthLayout>
  );
}
